import { useMemo, useState, type ReactElement } from "react";

import { melbourneDay } from "../melbourne.js";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * Cumulative profit, in units.
 *
 * One line, one meaning: what the bankroll is up or down since the first bet.
 * It takes its colour from the money language rather than a house hue - green
 * while ahead, red while behind - so the answer to "is this working" lands
 * before any axis is read.
 *
 * The series is already cumulative, so coarser buckets take the *last* value
 * in each period rather than summing. The running total on the last day of a
 * week is the running total for that week; summing would multiply the same
 * profit by the number of days it happened to survive.
 *
 * `day` must be a full ISO date. It was previously fed MM-DD, which parses as
 * a date in 2001 - monthly silently collapsed into daily, and weekly anchored
 * to the wrong year's weekdays.
 */

export type UnitsPoint = {
  readonly day: string;
  readonly units: number;
  /** What this individual bet was. Read only by the bet-by-bet grain, the one
   * view where a point is a bet rather than a period. */
  readonly caption?: string;
};

export type Grain = "bet" | "daily" | "weekly" | "monthly";

/**
 * When a bet's profit actually landed.
 *
 * The curve used to be ordered and dated by placement, which put the money on
 * the board before the game had finished: a bet placed on Monday night for a
 * game that runs Tuesday afternoon showed its win on Monday. The running
 * total on any given day was then not the bankroll that day. Settlement is
 * when the bankroll moved, so that is what the line is drawn against.
 *
 * Rows settled before settled_at was recorded fall back to placement, which
 * is where they already were.
 */
export function resolvedAt(row: {
  readonly placedAt: string;
  readonly settledAt?: string | null;
}): string {
  return row.settledAt !== null &&
    row.settledAt !== undefined &&
    row.settledAt !== ""
    ? row.settledAt
    : row.placedAt;
}

/**
 * How far back to plot. Separate from the grain on purpose: "the last three
 * months, bet by bet" and "the last three months, by week" are both questions
 * worth asking, and folding the two into one strip could not express either.
 */
export type Range = "day" | "week" | "month" | "quarter" | "year" | "custom";

export interface ChartState {
  readonly grain: Grain;
  readonly range: Range;
  readonly from: string;
  readonly to: string;
  readonly setGrain: (next: Grain) => void;
  readonly setRange: (next: Range) => void;
  readonly setFrom: (next: string) => void;
  readonly setTo: (next: string) => void;
}

/** Held by the page so the controls can sit in the tile header while the plot
 * keeps the whole body. */
export function useChartState(): ChartState {
  const [grain, setGrain] = useState<Grain>("daily");
  const [range, setRange] = useState<Range>("quarter");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  return { grain, range, from, to, setGrain, setRange, setFrom, setTo };
}

const GRAINS: readonly { readonly id: Grain; readonly label: string }[] = [
  { id: "bet", label: "Bet by bet" },
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
];

const RANGES: readonly { readonly id: Range; readonly label: string }[] = [
  { id: "day", label: "Today" },
  { id: "week", label: "Last 7 days" },
  { id: "month", label: "Last month" },
  { id: "quarter", label: "Last 3 months" },
  { id: "year", label: "Last year" },
  { id: "custom", label: "Custom period" },
];

/**
 * Grain on the left, range on the right.
 *
 * The range is a select rather than a second tab strip because ten buttons do
 * not fit beside a tile title - they wrap onto a second row and take the space
 * from the plot, which is the thing anyone came here to read. For the same
 * reason the custom period's two date fields sit above the plot rather than up
 * here, where they wrapped the header onto two rows.
 */
export function ChartControls({
  state,
}: {
  readonly state: ChartState;
}): ReactElement {
  return (
    <div className="chart-controls">
      <div className="grain-tabs" role="tablist" aria-label="Point grouping">
        {GRAINS.map((option) => (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={state.grain === option.id}
            className={state.grain === option.id ? "grain current" : "grain"}
            onClick={() => state.setGrain(option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="range-controls">
        <label className="range-picker">
          <span className="sr-only">Date range</span>
          <select
            value={state.range}
            onChange={(event) => state.setRange(event.target.value as Range)}
          >
            {RANGES.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

/** Today in Melbourne, which is the calendar the points are dated in. */
export function todayIso(now: Date = new Date()): string {
  return melbourneDay(now);
}

/**
 * The window a range asks for, as inclusive ISO bounds. An empty string means
 * unbounded.
 *
 * Only "today" closes the upper bound. The relative ranges mean "since N ago"
 * and deliberately leave it open, so a bet that settles late cannot fall off
 * the right-hand edge of its own range.
 *
 * Months are stepped by clamping rather than letting setUTCMonth roll over:
 * one month before 31 March is 28 February, not 3 March.
 */
export function rangeBounds(
  state: { readonly range: Range; readonly from: string; readonly to: string },
  today = todayIso(),
): { readonly from: string; readonly to: string } {
  if (state.range === "custom") return { from: state.from, to: state.to };
  if (state.range === "day") return { from: today, to: today };

  const end = new Date(today + "T00:00:00Z");
  const start = new Date(end);
  if (state.range === "week") start.setUTCDate(start.getUTCDate() - 6);
  else {
    const months =
      state.range === "month" ? 1 : state.range === "quarter" ? 3 : 12;
    const day = start.getUTCDate();
    start.setUTCDate(1);
    start.setUTCMonth(start.getUTCMonth() - months);
    const lastOfMonth = new Date(
      Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0),
    ).getUTCDate();
    start.setUTCDate(Math.min(day, lastOfMonth));
  }
  return { from: start.toISOString().slice(0, 10), to: "" };
}

/** The tooltip shows the whole label; the axis shows only what precedes this. */
const SEPARATOR = "  ·  ";

export function axisTick(value: string): string {
  const cut = value.indexOf(SEPARATOR);
  return cut === -1 ? value : value.slice(0, cut);
}

/**
 * Day keys are Melbourne calendar dates, and the UTC arithmetic below is how
 * you do date maths on a plain date: parsing "2026-09-08" at UTC midnight and
 * reading it back in UTC returns the same date in every timezone the browser
 * might be in. These never touch an instant.
 */
function bucketKey(day: string, grain: Grain): string {
  const parsed = new Date(day);
  if (Number.isNaN(parsed.getTime())) return day;
  if (grain === "monthly") return day.slice(0, 7);
  if (grain === "weekly") {
    // Monday-anchored, so a week reads the way a betting week does.
    const monday = new Date(parsed);
    const offset = (monday.getUTCDay() + 6) % 7;
    monday.setUTCDate(monday.getUTCDate() - offset);
    return monday.toISOString().slice(0, 10);
  }
  return day;
}

function label(day: string, grain: Grain): string {
  const parsed = new Date(day);
  if (Number.isNaN(parsed.getTime())) return day;
  if (grain === "monthly")
    return parsed.toLocaleDateString("en-AU", {
      month: "short",
      year: "2-digit",
      timeZone: "UTC",
    });
  return parsed.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/**
 * What one bet was, for the bet-by-bet tooltip. The line already carries the
 * running total, so this carries what moved it: the bet, its price, and what
 * it returned.
 */
export function betCaption(row: {
  readonly description: string;
  readonly actualPrice: number;
  readonly result: "WIN" | "LOSS" | "PUSH" | "VOID" | null;
  readonly profitUnits: number | null;
}): string {
  const price = Number.isFinite(row.actualPrice)
    ? ` @ ${row.actualPrice.toFixed(2)}`
    : "";
  if (row.result === null) return `${row.description}${price} - open`;
  const profit = row.profitUnits ?? 0;
  const signed = `${profit >= 0 ? "+" : ""}${profit.toFixed(2)}u`;
  return `${row.description}${price} - ${row.result} ${signed}`;
}

export interface PlotPoint {
  readonly label: string;
  readonly units: number;
}

/** Kept out of the component so the bucketing can be tested without a DOM. */
export function chartPoints(
  data: readonly UnitsPoint[],
  state: { grain: Grain; range: Range; from: string; to: string },
  today = todayIso(),
): PlotPoint[] {
  const { grain } = state;
  const { from, to } = rangeBounds(state, today);
  const inRange = data.filter((point) => {
    if (from !== "" && point.day < from) return false;
    if (to !== "" && point.day > to) return false;
    return true;
  });
  // One point per bet, nothing collapsed. Every other grain answers "where was
  // the bankroll on this date"; this one answers "what did each bet do".
  if (grain === "bet")
    return inRange.map((point, index) => ({
      label:
        label(point.day, "daily") +
        SEPARATOR +
        (point.caption ?? `Bet ${index + 1}`),
      units: point.units,
    }));
  const byBucket = new Map<string, UnitsPoint>();
  for (const point of inRange) byBucket.set(bucketKey(point.day, grain), point);
  return [...byBucket.values()].map((point) => ({
    label: label(point.day, grain),
    units: point.units,
  }));
}

export function UnitsChart({
  data,
  state,
}: {
  readonly data: readonly UnitsPoint[];
  readonly state: ChartState;
}): ReactElement {
  // useChartState returns a fresh object each render, so memoise on values.
  const { grain, range, from, to } = state;
  const points = useMemo(
    () => chartPoints(data, { grain, range, from, to }),
    [data, from, grain, range, to],
  );

  // Green above break-even, red below, split exactly at zero (Moss, 3 Nov
  // 2026: a run that was up showed red because the latest point was down).
  // The gradients span the line's own extent, so zero sits at this offset.
  const high = Math.max(0, ...points.map((point) => point.units));
  const low = Math.min(0, ...points.map((point) => point.units));
  const zero = high - low === 0 ? 1 : high / (high - low);
  const signColour = (units: number) =>
    units >= 0 ? "var(--positive)" : "var(--negative)";

  return (
    <>
      {range === "custom" ? (
        <div className="grain-range">
          <label>
            <span>From</span>
            <input
              type="date"
              value={from}
              onChange={(event) => state.setFrom(event.target.value)}
            />
          </label>
          <label>
            <span>To</span>
            <input
              type="date"
              value={to}
              onChange={(event) => state.setTo(event.target.value)}
            />
          </label>
        </div>
      ) : null}
      <div className="chart-card">
        <div className="chart" aria-label="Cumulative profit units chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={points}
              margin={{ top: 8, right: 8, bottom: 0, left: -18 }}
            >
              <defs>
                <linearGradient id="units-stroke" x1="0" y1="0" x2="0" y2="1">
                  <stop offset={zero} stopColor="var(--positive)" />
                  <stop offset={zero} stopColor="var(--negative)" />
                </linearGradient>
                <linearGradient id="units-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset={0}
                    stopColor="var(--positive)"
                    stopOpacity={0.3}
                  />
                  <stop
                    offset={zero}
                    stopColor="var(--positive)"
                    stopOpacity={0.04}
                  />
                  <stop
                    offset={zero}
                    stopColor="var(--negative)"
                    stopOpacity={0.04}
                  />
                  <stop
                    offset={1}
                    stopColor="var(--negative)"
                    stopOpacity={0.3}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "var(--muted)", fontSize: 11 }}
                minTickGap={24}
                tickFormatter={axisTick}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: "var(--muted)", fontSize: 11 }}
                width={52}
              />
              {/* Break-even is the only line on this chart worth drawing. */}
              <ReferenceLine
                y={0}
                stroke="var(--muted)"
                strokeDasharray="3 4"
              />
              <Tooltip
                cursor={{ stroke: "var(--muted)", strokeDasharray: "3 4" }}
                contentStyle={{
                  background: "#1b2330",
                  border: "1px solid #232d3b",
                  borderRadius: 10,
                  color: "#e9eef4",
                  fontSize: 12,
                }}
                labelStyle={{ color: "#8a97a8" }}
                formatter={(value: number) => [
                  `${value >= 0 ? "+" : ""}${value.toFixed(2)}u`,
                  "Cumulative",
                ]}
              />
              <Area
                type="monotone"
                dataKey="units"
                stroke="url(#units-stroke)"
                strokeWidth={2}
                fill="url(#units-fill)"
                dot={(props: {
                  cx?: number;
                  cy?: number;
                  index?: number;
                  payload?: { units: number };
                }) => (
                  <circle
                    key={props.index}
                    cx={props.cx}
                    cy={props.cy}
                    r={3}
                    fill="var(--canvas)"
                    stroke={signColour(props.payload?.units ?? 0)}
                    strokeWidth={2}
                  />
                )}
                activeDot={(props: {
                  cx?: number;
                  cy?: number;
                  payload?: { units: number };
                }) => (
                  <circle
                    cx={props.cx}
                    cy={props.cy}
                    r={5}
                    fill={signColour(props.payload?.units ?? 0)}
                    stroke="var(--canvas)"
                  />
                )}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}
